import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MessageCircle, Share2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { SOCIAL_API, Comments } from "@/components/community/PostSocial";
import { PostMenu, sharePost } from "@/components/feed/PostMenu";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";

const total = (r) => Object.values(r || {}).reduce((s, n) => s + (n || 0), 0);
const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n || 0));

function RailButton({ onClick, label, testid, children, active }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} data-testid={testid} data-active={active}
            className="flex flex-col items-center gap-0.5 text-white drop-shadow-md active:scale-90 transition-transform">
      {children}
    </button>
  );
}

/** Coluna de ações à direita: autor, reação (coração), comentários, partilha, menu ⋯ */
export function FeedActionRail({ item, user, base, onRemoved, onOpenComments, commentsCount }) {
  const cfg = SOCIAL_API[base];
  const [state, setState] = useState({ reactions: item.reactions || {}, mine: item.mine ?? null });
  useEffect(() => setState({ reactions: item.reactions || {}, mine: item.mine ?? null }), [item.reactions, item.mine]);
  const love = async () => {
    if (!user) { toast.info("Inicia sessão para reagir"); return; }
    if (!item.can_interact) { toast.info(base === "community" ? "Entra na comunidade para reagir" : cfg.lockedReact); return; }
    try { const { data } = await api.post(cfg.toggle, { target_type: "post", target_id: item.id, type: state.mine || "love" }); setState({ reactions: data.reactions, mine: data.mine }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro"); }
  };
  const t = `feed-rail-${item.type}-${item.id}`;
  return (
    <div className="flex flex-col items-center gap-5" data-testid={t}>
      {!item.system_tag && item.author?.id && (
        <Link to={`/membro/${item.author.id}`} data-testid={`${t}-author`} className="rounded-full ring-2 ring-white shadow-lg">
          <Avatar src={item.author.photo_url} name={item.author.name} size={46} />
        </Link>
      )}
      <RailButton onClick={love} label="Reagir" testid={`${t}-love`} active={!!state.mine}>
        <Heart size={32} className={state.mine ? "text-[#c8102e]" : ""} fill={state.mine ? "currentColor" : "none"} strokeWidth={2} />
        <span className="text-xs font-semibold" data-testid={`${t}-love-count`}>{fmt(total(state.reactions))}</span>
      </RailButton>
      <RailButton onClick={onOpenComments} label="Comentários" testid={`${t}-comments`}>
        <MessageCircle size={32} fill="currentColor" className="text-white [&>path]:stroke-white" strokeWidth={1.5} />
        <span className="text-xs font-semibold" data-testid={`${t}-comments-count`}>{fmt(commentsCount)}</span>
      </RailButton>
      <RailButton onClick={() => sharePost(item)} label="Partilhar" testid={`${t}-share`}>
        <Share2 size={30} strokeWidth={2} />
      </RailButton>
      <PostMenu item={item} user={user} onRemoved={onRemoved} dark />
    </div>
  );
}

/** Comentários num painel que sobe por cima da publicação (fecha ao deslizar para baixo). */
export function FeedCommentsSheet({ item, user, base, open, onOpenChange, onCount }) {
  const isCommunity = base === "community";
  const change = (o) => { onOpenChange(o); if (!o) { document.activeElement?.blur?.(); setTimeout(() => window.scrollTo(0, 0), 50); setTimeout(() => window.scrollTo(0, 0), 400); } };
  return (
    <Drawer open={open} onOpenChange={change} shouldScaleBackground={false}>
      <DrawerContent className="max-h-[80dvh] rounded-t-2xl md:max-w-lg md:mx-auto" data-testid={`feed-comments-sheet-${item.id}`}>
        <DrawerHeader className="py-2 text-center">
          <DrawerTitle className="text-sm font-bold">Comentários</DrawerTitle>
          <DrawerDescription className="sr-only">Comentários desta publicação</DrawerDescription>
        </DrawerHeader>
        <div className="px-4 overflow-y-auto overscroll-contain" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          {open && <Comments post={item} user={user} canInteract={item.can_interact} isStaff={user?.role === "admin"} isPostOwner={!isCommunity && item.is_mine} base={base} onCount={onCount} stickyInput
                             contextLabel={isCommunity ? item.source?.name : (item.source?.on_profile === false ? "Feed geral" : "Perfil / Feed geral")} />}
          {isCommunity && !item.can_interact && <Link to={item.source.link} className="mt-2 inline-block text-xs font-semibold text-[#c8102e]" data-testid={`feed-join-${item.id}`}>Entrar na comunidade para interagir</Link>}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
